import React, { useEffect, useState } from 'react';
import { Box, Grid, IconButton, Drawer, Tooltip } from '@mui/material';
import {
    BreadCrumbs,
    LoadingBar,
    PageTitleWithToolTip,
} from '../Common';
import AnalyticsMenuButton from '../AnalyticsMenuButton';
import Routes from '../../routes';
import { ArrowBackIosOutlined, KeyboardTab } from '@mui/icons-material';
import AnalyticReportOptions from './AnalyticReportOptions';
import AnalyticReportContainer from './AnalyticReportContainer';
import Placeholder from './Placholder';


const Analytics = () => {
    const [loading, setLoading] = useState(true);
    const [path, setPath] = useState([]);
    const [reports, setReports] = useState(null);
    const [powerBiReports, setPowerBiReports] = useState([]);
    const [accessToken, setAccessToken] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);
    const [breadCrumbs, setBreadCrumbs] = useState([{ text: "Analytics", tooltip: "Access a complete range of reports and tools to assist with operational, sustainability, and regulatory tasks." }]);
    const [optionNavVisibile, setOptionNavVisibile] = useState(false);

    const containerStyle = {
        flexGrow: 1,
        position: 'relative',
        marginLeft: (optionNavVisibile ? '12.5vw' : '0vw'),
        width: optionNavVisibile ? 'calc(100% - 12vw)' : 'calc(100%)',
    };

    useEffect(() => {
        fetchData();
        fetchPowerBiReports();
    }, []);

    const mapBreadcrumbs = (sections) => {
        const breadcrumbs = [{
            text: "Analytics",
            tooltip: "Access a complete range of reports and tools to assist with operational, sustainability, and regulatory tasks.",
            onClick: () => navigateBack(0)
        }];
        for (let i = 1; i < sections.length; i++) {
            const subsectionNames = sections[i].map(subsection => ({
                text: subsection.name,
                tooltip: subsection.tooltip,
                onClick: sections.length - 1 === i ? null : () => navigateBack(i)
            }));
            breadcrumbs.push(...subsectionNames);
        }
        return breadcrumbs;
    };

    const fetchPowerBiReports = async () => {
        try {
            const response = await fetch(Routes.PowerBI.GetAnalyticReports());
            if (!response.ok) {
                throw new Error("Failed to fetch reports");
            }
            const data = await response.json();
            setPowerBiReports(Array.isArray(data.reports) ? [...data.reports] : []);
            setAccessToken(data.accessToken);
        } catch (error) {
            console.log("fetchPowerBiReports:", error);
        }
    };

    const fetchData = async () => {
        try {
            const response = await fetch(Routes.PowerBI.GetReportSections());
            const data = await response.json();

            // Recursively find subsections
            const mapSections = (sections, parentId = null) => {
                return sections
                    .filter(section =>
                        parentId === null
                            ? !section.hasOwnProperty('reportSectionId')
                            : section.reportSectionId === parentId
                    )
                    .map(section => ({
                        ...section,
                        subSections: mapSections(sections, section.id)
                    }));
            };

            const mappedData = mapSections(data);

            setPath([mappedData]);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    const fetchReport = async (sectionId) => {
        try {
            setLoading(true);
            const response = await fetch(Routes.PowerBI.GetReportBySectionsId(sectionId));
            const data = await response.json();
            setReports(data !== undefined ? data : []);
            setOptionNavVisibile(true);
            setLoading(false);
        } catch (error) {
            console.log('fetchReport:', error);
        }
    };

    useEffect(() => {
        const currentPath = path.length > 1 ? path[path.length - 1][0] : null;
        if (currentPath) {
            if (currentPath.subSections.length === 0) {
                reports === null ? fetchReport(currentPath.id) : selectedReport && setSelectedReport(null);
            } else {
                clearReport();
            }
        } else {
            clearReport();
        }

        setBreadCrumbs(mapBreadcrumbs(path));
    }, [path]);

    const navigateTo = (section) => {
        setPath((prevPath) => [...prevPath, [section]]);
    };

    const navigateBack = (index) => {
        setPath((prevPath) => prevPath.slice(0, index !== undefined ? index + 1 : path.length - 1));
    };

    const clearReport = () => {
        setReports(null);
        setSelectedReport(null);
        setOptionNavVisibile(false);
    };

    const getTooltipText = () => {
        if (selectedReport) {
            return selectedReport.tooltip;
        }

        return breadCrumbs[breadCrumbs.length - 1].tooltip;
    };

    const SectionMenu = () => {
        if (path.length === 0) {
            return;
        }
        const currentPath = path.length === 1 ? path[0] : path[path.length - 1][0].subSections;
        return currentPath.map(section => (
            <AnalyticsMenuButton
                key={section.id}
                title={section.name}
                icon={section.icon}
                toolTipText={section.tooltip}
                onClick={() => {
                    navigateTo(section);
                    breadCrumbs.push({ text: section.name, toolTipText: section.toolTipText });
                }}
            />
        ));
    };

    if (loading) {
        return <LoadingBar />;
    }

    const reportOptionsContainerHeight = (reports !== null && selectedReport !== null) ? '100%' : '86vh';

    return (
        <>
            <Drawer
                hideBackdrop
                variant='persistent'
                anchor="left"
                elevation={0}
                open={optionNavVisibile}
                transitionDuration={0}
                onClose={() => setOptionNavVisibile(visible => !visible)}
                sx={{
                    width: '13vw',
                    [`& .MuiDrawer-paper`]: {
                        width: '13vw',
                        height: reportOptionsContainerHeight,
                        minHeight: '86vh',
                        boxSizing: 'border-box',
                        position: "absolute",
                    },
                }}
            >
                {reports !== null && <AnalyticReportOptions
                    reports={reports}
                    breadCrumbs={breadCrumbs}
                    selectedReport={selectedReport}
                    setSelectedReport={setSelectedReport}
                    pathLength={path.length}
                    navigateBack={navigateBack}
                />}
            </Drawer>
            <div style={{
                position: 'absolute',
                left: optionNavVisibile ? '13vw' : '2px',
                top: '2px',
                maxWidth: '10%',
            }}>
                {
                    reports !== null &&
                    <Tooltip title={optionNavVisibile ? "Collapse List" : "Open List"}>
                        <IconButton
                            sx={{
                                ":focus": {
                                    outline: "none"
                                },
                            }}
                            onClick={() => setOptionNavVisibile(visible => !visible)}
                        >
                            <KeyboardTab
                                size={22}
                                sx={{
                                    transform: optionNavVisibile ? "scaleX(-1)" : "scaleX(1)",
                                    '& .MuiButtonBase-root': { outline: 'none', padding: 0 }
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                }
            </div>

            <Box component="main" sx={containerStyle}>
                <div Style={"margin-top:20px"}></div>
                <BreadCrumbs
                    path={breadCrumbs}
                />
                <Grid
                    container
                    direction="row"
                    alignItems="center">
                        
                    {
                        !selectedReport  && (
                            <PageTitleWithToolTip
                                pageTitle={"Analytics"}
                                toolTipText={"Access a complete range of reports and tools to assist with operational, sustainability, and regulatory tasks."}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    whiteSpace: 'nowrap'
                                }}
                            />
                        )
                    }

                    {(selectedReport && !selectedReport.powerBiLink) && //we only display the title and tooltip for non-power bi reports

                        <Grid item xs={8} md={8} lg={8} mb={3} display="flex" alignItems="center" justifyContent="flex-start">
                            {/*{(path.length > 1) && */}
                            {/*    <IconButton */}
                            {/*        size='small' */}
                            {/*        sx={{*/}
                            {/*            marginRight: "4px",*/}
                            {/*            ":focus": {*/}
                            {/*                outline: "none"*/}
                            {/*            },*/}
                            {/*        }} */}
                            {/*        onClick={() => { navigateBack();}}>*/}
                            {/*        <ArrowBackIosOutlined sx={{ "&.MuiSvgIcon-root": { fontSize: "20px", color: "#000000" } }} />*/}
                            {/*    </IconButton>*/}
                            {/*}*/}

                            <PageTitleWithToolTip
                                pageTitle={breadCrumbs[breadCrumbs.length - 1].text}
                                toolTipText={getTooltipText()}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    whiteSpace: 'nowrap'
                                }}
                            />
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <div style={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: "20px"
                        }}>
                            {reports === null && <SectionMenu />}
                        </div>
                    </Grid>
                </Grid>

                {selectedReport !== null &&
                    (
                        <AnalyticReportContainer
                            trakrefReports={reports}
                            breadCrumbs={breadCrumbs}
                            selectedReport={selectedReport}
                            powerBiReports={powerBiReports}
                            accessToken={accessToken}
                        />)
                }
                {reports !== null && selectedReport === null &&
                    <Placeholder
                        message='Please select a report from the left menu'
                        subMessage=''
                    />
                }
            </Box>
        </>
    );
};

export default Analytics;
